import * as Yup from "yup";
import { validateIbanCheckNew } from "../lib/Common/validateiban";

const validateMaloID = (id) => {
  if (!id || id.length !== 11) return false;
  //1234567890 5   25+20*2 => 65 => Math.ceil(65/10)*7 => 70 => 70 - 65
  //3214567890 5   98765432105
  const r = Number(id.slice(-1)); // Last digit (checksum)
  const n = id.slice(0, -1).split("").map(Number); // First 10 digits

  const evenIndicesSum = n
    .filter((_, index) => index % 2 === 0)
    .reduce((sum, digit) => sum + digit, 0);

  const oddIndicesSum = n
    .filter((_, index) => index % 2 === 1)
    .reduce((sum, digit) => sum + digit, 0);

  const total = evenIndicesSum + oddIndicesSum * 2;

  let checksum = Math.ceil(total / 10) * 10 - total;
  if (checksum === 10) checksum = 0;

  return r === checksum;
};

export const numeratorSchema = Yup.object({
  postalCode: Yup.string()
    .min(5, "Postal code must be exactly 5 digits")
    .test("is-digits", "Postal code must be exactly 5 digits", (value) =>
      /^\d+$/.test(value)
    )
    .required("Postal code is required"),
  validStreet: Yup.boolean(),
  hasSmartMeter: Yup.boolean().nullable().required("Smart meter is required"),
  street: Yup.string().when("validStreet", {
    is: true,
    then: (schema) => schema.trim().required("Street Address is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  customerType: Yup.string()
    .oneOf(["private", "business"])
    .required("Customer type is required"),
  energyConsumption: Yup.string().when("customerType", {
    is: (val) => val === "private" || val === "business",
    then: (schema) =>
      schema
        .matches(/^[0-9]+(.[0-9]+)*$/, "Only digits are allowed")
        .matches(
          /^(?:[0-9]{1,3}(?:\.[0-9]{3})*(?:,[0-9]+)?|[0-9]{1,3}(?:,[0-9]{3})*(?:\.[0-9]+)?|[0-9]+(?:[.,][0-9]+)?)$/,
          "Energy consumption is invalid"
        )
        .test(
          "is-greater-than-1.5",
          "Energy consumption must be greater than 1.500",
          (value) => {
            if (!value) return false;

            // Remove dots (thousands separators), replace comma with dot for decimal
            const normalized = value.replace(/\./g, "").replace(",", ".");

            const numericValue = parseFloat(normalized);
            // console.log(normalized, "normalized");
            return numericValue >= 1500;
          }
        )
        .max(17, "Maximum 13 characters allowed")
        .required("Energy consumption is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  email: Yup.string()
    .email("Invalid email address")
    .matches(
      /^[a-zA-Z0-9._-]+@([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/,
      "Invalid email address"
    ),
});

export const contractSchema = (customerType) => {
  const phoneRegExp =
    /^(\+?49|0049|0)[ -]?(\d{1,4}[ -]?){1,4}\d{1,4}$|^(\+?353|00353|0)[ -]?(\d{1,4}[ -]?){1,4}\d{1,4}$/;
  return Yup.object({
    salutation: Yup.string().required("Salutation is required"),
    first_name: Yup.string().trim().required("First name is required"),
    last_name: Yup.string().trim().required("Last name is required"),
    tax_number: Yup.string()
      .trim()
      .max(13, "Tax number must be at most  13 characters")
      .nullable(),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required")
      .matches(
        /^[a-zA-Z0-9._-]+@([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/,
        "Invalid email address"
      ),
    ...(customerType === "business" && {
      company_name: Yup.string().trim().required("Company name is required."),
    }),
    existing: Yup.boolean().required("Please select Yes or No"),
    consumer_id: Yup.string().when("existing", {
      is: true,
      then: (schema) => schema.trim().required("Consumer ID is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    username: Yup.string().when("existing", {
      is: false,
      then: (schema) => schema.trim().required("Username is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    telephone: Yup.string()
      .trim()
      .nullable()
      .notRequired()
      .test("phone-valid", "Phone number is not valid", (value) => {
        if (!value) return true; // allow empty/null
        return phoneRegExp.test(value);
      }),
  });
};

export const deliveryAddressSchema = Yup.object({
  deliv_address_street: Yup.string().trim().required("Street is required"),
  deliv_address_house_number: Yup.string()
    .trim()
    .max(5, "House number must be at most 5 numbers")
    // .matches(/^\d+$/, "House number must contain only numbers")
    .required("House number is required"),
  deliv_address_zipcode: Yup.string()
    .trim()
    .required("Postal code is required")
    .matches(/^\d{5}$/, "Postal code must be exactly 5 digits"),
  deliv_address_city: Yup.string().trim().required("City is required"),
  deliv_address_addition: Yup.string(), // Optional

  differentBillingAddress: Yup.boolean(),

  bill_address_street: Yup.string().when("differentBillingAddress", {
    is: true,
    then: (schema) => schema.trim().required("Street is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  bill_address_house_number: Yup.string().when("differentBillingAddress", {
    is: true,
    then: (schema) =>
      schema
        .max(5, "House number must be at most 5 numbers")
        .trim()
        // .matches(/^\d+$/, "House number must contain only numbers")
        .required("House number is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  accounting_email: Yup.string().when("differentBillingAddress", {
    is: true,
    then: (schema) =>
      schema
        .email("Invalid email")
        .required("Email is required")
        .matches(
          /^[a-zA-Z0-9._-]+@([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/,
          "Invalid email address"
        ),
  }),
  bill_address_zipcode: Yup.string().when("differentBillingAddress", {
    is: true,
    then: (schema) =>
      schema
        .trim()
        .required("Postal code is required")
        .matches(/^\d{5}$/, "Postal code must be exactly 5 digits"),
    otherwise: (schema) => schema.notRequired(),
  }),
  bill_address_city: Yup.string().when("differentBillingAddress", {
    is: true,
    then: (schema) => schema.trim().required("City is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  bill_address_addition: Yup.string(), // Optional
});

export const exchangeSchema = (minDate, custype) => {
  let safeMinDate = new Date(minDate); // ensure it's a valid Date
  if (minDate)
    safeMinDate = new Date(safeMinDate.setDate(safeMinDate.getDate() - 1));
  const isPrivate = custype === "private";
  return Yup.object({
    subscription_reason: Yup.string().required(
      "Subscription reason is required"
    ),
    preferred_delivery_start: Yup.date()
      .required("Delivery date is required")
      .min(
        safeMinDate,
        `Delivery date cannot be before ${
          safeMinDate ? safeMinDate?.toLocaleDateString() : ""
        }`
      ),
    meterId: Yup.string()
      .trim()
      .min(4, "Meter id must be at least 4 characters")
      .max(16, "Meter id must be at most 16 characters")
      .matches(/\d/, "Meter id must contain at least one number")
      .matches(/^[^!#~$%&/()=]*$/, "Meter id invalid format")
      .when([], {
        is: () => isPrivate,
        then: (schema) => schema.required("Meter id is required."),
        otherwise: (schema) => schema.notRequired(),
      }),
    meteringPointId: Yup.string()
      .trim()
      .when([], {
        is: () => isPrivate,
        then: (schema) => schema.required("Meteringpoint id is required."),
        otherwise: (schema) => schema.notRequired(),
      }),
    previous_provider: Yup.string().when("subscription_reason", {
      is: "E03",
      then: (schema) => schema.required("Current provider is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });
};

export const paymentSchema = Yup.object({
  payment_method: Yup.string().required("Payment method is required"),

  // Debit Card Fields
  bank_data_first_name: Yup.string().when("payment_method", {
    is: "direct_debit",
    then: (schema) => schema.required("First name is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  bank_data_last_name: Yup.string().when("payment_method", {
    is: "direct_debit",
    then: (schema) => schema.required("Last name is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  bank_data_iban: Yup.string().when("payment_method", {
    is: "direct_debit",
    then: (schema) =>
      schema
        .required("IBAN is required")
        .matches(/^[^!@#~$%&/()=]*$/, "Invalid IBAN number")
        .test("is-valid-iban", "Invalid IBAN number", function (value) {
          if (!value) return false; // required check already handled
          return validateIbanCheckNew(value);
        }),
    otherwise: (schema) => schema.notRequired(),
  }),
  authorizePayments: Yup.bool().when("payment_method", {
    is: "direct_debit",
    then: (schema) =>
      schema
        .oneOf([true], "Please check this box to continue.")
        .required("Please check this box to continue."),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export const ReviewOrderSchema = Yup.object({
  termsAccepted: Yup.bool()
    .oneOf([true], "You must accept the terms and conditions")
    .required("You must accept the terms and conditions"),
});
